import { useEffect, useMemo, useState } from 'react';
import { App, Button, Drawer, Flex, Segmented, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { ERROR_MESSAGE_KEY, toHttpError } from '@/shared/api/errors';
import { TAGS, qk } from '@/shared/query/keys';
import { useMutation } from '@/shared/query/useMutation';
import { useQuery } from '@/shared/query/useQuery';
import { ErrorState, TableEmpty } from '@/shared/ui/primitives';
import { fetchNotices, markNoticeRead, NOTICES_PAGE_SIZE, type Notice } from './api';
import { NoticeRow } from './NotificationList';

/** 列表顶部的筛选档——「全部 / 仅未读」。 */
type Filter = 'all' | 'unread';

/** 一页的累积。分页按页走 ⇒ 翻过的页都留着，用户往回翻不用重取。 */
interface PageChunk {
  page: number;
  total: number;
  items: readonly Notice[];
}

/**
 * 「全部已读」最多跑几轮。
 *
 * ⚠️ 这是**防跑飞**的上限，不是业务上限：每轮至少标掉一批未读行 ⇒ 正常情况
 * 几十轮内就结束。万一某轮标了却没生效（行没真变已读），下一轮会取回同一批
 * ⇒ 不设上限就是死循环。
 */
const MAX_MARK_ALL_ROUNDS = 50;

/**
 * 通知列表（抽屉里的那一屏）。
 *
 * **分页用后端给的 `page` / `total`**：一页一页取，翻过的页累积在本地 ⇒
 * 「加载更多」不是重取前 N 条，而是真往后翻（后端的 `size` 有上限 200，
 * 一直加大迟早撞 ceiling）。
 */
export function NotificationDrawer({
  open,
  unread,
  onClose,
}: {
  open: boolean;
  /** 全局未读数——「全部已读」在没有未读时不可用。 */
  unread: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const [chunks, setChunks] = useState<readonly PageChunk[]>([]);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const unreadOnly = filter === 'unread';

  const list = useQuery(
    async () => {
      const data = await fetchNotices({ page, size: NOTICES_PAGE_SIZE, unreadOnly });
      // 把页码带出来：ahooks 换 cacheKey 时可能还拿着上一页的 data，
      // 靠这个字段分得清「这份数据是哪一页的」。
      return { page, total: data.total, items: data.items };
    },
    {
      key: qk.notificationsList({ page, size: NOTICES_PAGE_SIZE, unreadOnly }),
      tags: [TAGS.notifications],
      ready: open,
    },
  );

  useEffect(() => {
    const data = list.data;
    if (data === undefined) return;
    setChunks((prev) => [
      ...prev.filter((chunk) => chunk.page !== data.page),
      { page: data.page, total: data.total, items: data.items },
    ]);
  }, [list.data]);

  const rows = useMemo(
    () =>
      chunks
        .filter((chunk) => chunk.page <= page)
        .sort((a, b) => a.page - b.page)
        .flatMap((chunk) => chunk.items),
    [chunks, page],
  );
  // `total` 是**整个筛选集**的条数（不是这一页的）⇒ 取第一页那份。
  const total = chunks.find((chunk) => chunk.page === 1)?.total ?? 0;

  const changeFilter = (next: Filter): void => {
    // 换筛选 ⇒ 页码与累积都重置：旧筛选的行不能混进新列表。
    setFilter(next);
    setPage(1);
    setChunks([]);
  };

  const markRead = useMutation((id: string) => markNoticeRead(id), {
    invalidate: [TAGS.notifications],
    onSuccess: (updated) => {
      // 本地把这一行改掉：失效标签只会重拉**当前页**，别的页里的同一行不会自动更新。
      setChunks((prev) =>
        prev.map((chunk) => ({
          ...chunk,
          items: unreadOnly
            ? // 「仅未读」模式下，标已读 = 这一行该从列表里消失。
              chunk.items.filter((item) => item.id !== updated.id)
            : chunk.items.map((item) => (item.id === updated.id ? updated : item)),
        })),
      );
    },
    // 成功**不提示**（逐行点击，弹一次太吵；行本身会消失/变已读）；
    // 失败**必须提示**——`fire()` 吞异常 ⇒ 不写 onError 就是静默失败。
    onError: (error) => message.error(t(ERROR_MESSAGE_KEY[error.code]), 6),
  });

  const markOne = (id: string): void => {
    setMarkingId(id);
    void markRead
      .run(id)
      .catch(() => undefined)
      .finally(() => setMarkingId(null));
  };

  const markAllRead = useMutation(
    async () => {
      // 后端**只有单行标已读**（`POST /notifications/{id}/read`）⇒ 「全部已读」
      // = 把未读行逐个标掉。每轮都取**第一页**未读：标掉一批，下一轮的第一页
      // 就是下一批（按页翻会跳行 ⇒ 只能一直取第一页）。
      let marked = 0;
      for (let round = 0; round < MAX_MARK_ALL_ROUNDS; round += 1) {
        const data = await fetchNotices({ page: 1, size: NOTICES_PAGE_SIZE, unreadOnly: true });
        if (data.items.length === 0) break;
        await Promise.all(data.items.map((row) => markNoticeRead(row.id)));
        marked += data.items.length;
        if (data.items.length < NOTICES_PAGE_SIZE) break;
      }
      return marked;
    },
    {
      invalidate: [TAGS.notifications],
      onSuccess: () => {
        setPage(1);
        setChunks([]);
        message.success(t('notifications.allReadDone'));
      },
      // ⚠️ 失败必须有提示：`useMutation.fire()` 会 `.catch(() => undefined)`
      // **吞掉异常** ⇒ 不写 `onError` 就是 100% 静默失败（用户点了「全部已读」
      // 却什么都没发生，且列表也没变）。
      onError: (error) => message.error(t(ERROR_MESSAGE_KEY[error.code]), 6),
    },
  );

  const error = list.error === undefined || list.error === null ? null : toHttpError(list.error);
  const hasMore = rows.length < total;

  return (
    <Drawer title={t('notifications.title')} open={open} onClose={onClose} size={480}>
      <Flex vertical gap="small" style={{ height: '100%' }}>
        <Flex align="center" justify="space-between" gap="small" wrap>
          <Segmented<Filter>
            value={filter}
            onChange={changeFilter}
            options={[
              { value: 'all', label: t('notifications.all') },
              { value: 'unread', label: t('notifications.unreadOnly') },
            ]}
          />
          <Button
            size="small"
            loading={markAllRead.loading}
            disabled={unread === 0}
            onClick={() => markAllRead.fire()}
          >
            {t('notifications.markAllRead')}
          </Button>
        </Flex>
        {error === null ? null : <ErrorState error={error} onRetry={() => list.refresh()} />}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {rows.length === 0 ? (
            list.loading ? (
              <Flex justify="center" style={{ padding: 24 }}>
                <Spin />
              </Flex>
            ) : (
              <TableEmpty label={t('notifications.empty')} />
            )
          ) : (
            <Flex vertical gap={8}>
              {rows.map((row) => (
                <NoticeRow
                  key={row.id}
                  row={row}
                  marking={markingId === row.id}
                  onMarkRead={markOne}
                />
              ))}
            </Flex>
          )}
        </div>
        {hasMore ? (
          <Button block loading={list.loading} onClick={() => setPage((prev) => prev + 1)}>
            {t('notifications.loadMore')}
          </Button>
        ) : null}
      </Flex>
    </Drawer>
  );
}
